import moment from 'moment';
import type { Event, Events } from '@interfaces/timeline';
import type { GroupedMachineEvent } from '@api/domain/types';
import type { MergeTimelineEventsParams } from '@components/machines/components/timeline/types';

const toEvent = (item: GroupedMachineEvent, start: string, end: string): Event => {
  let startedOn = item.startedOn ?? start;
  let endedOn = item.endedOn ?? end;

  if (moment.utc(startedOn).isBefore(moment.utc(start))) {
    startedOn = start;
  }
  if (moment.utc(endedOn).isAfter(moment.utc(end))) {
    endedOn = end;
  }

  return {
    status: item.status ?? '',
    subStatus: item.subStatus ?? '',
    startedOn,
    endedOn,
    sequenceNumberStartedOn: item.sequenceNumberStartedOn ?? 0,
    sequenceNumberEndedOn: item.sequenceNumberEndedOn ?? 0,
    durationInSeconds: moment.utc(endedOn).diff(moment.utc(startedOn), 'seconds'),
    ncFileMain: item.ncFileMain ?? '',
    ncFileStatus: item.ncFileStatus ?? '',
    quantityDelta: item.quantityDelta ?? 0,
    productionBatchId: item.productionBatchId ?? 0,
  };
};

const getEventKey = (event: Event): string =>
  `${event.sequenceNumberStartedOn}_${event.sequenceNumberEndedOn}`;

const groupEvents = (events: Event[]) => {
  const groupedByNCFile: Event[] = [];
  const groupedByBatch: Event[] = [];

  events.forEach((element) => {
    const lastGroupedItemByNCFile = groupedByNCFile[groupedByNCFile.length - 1];
    const lastGroupedItemByBatch = groupedByBatch[groupedByBatch.length - 1];

    if (!lastGroupedItemByNCFile || lastGroupedItemByNCFile.ncFileMain !== element.ncFileMain) {
      groupedByNCFile.push(element);
    } else {
      groupedByNCFile[groupedByNCFile.length - 1] = {
        ...lastGroupedItemByNCFile,
        durationInSeconds: element.durationInSeconds + lastGroupedItemByNCFile.durationInSeconds,
        endedOn: element.endedOn,
      };
    }

    if (
      !lastGroupedItemByBatch ||
      (element.productionBatchId != null && element.productionBatchId !== lastGroupedItemByBatch.productionBatchId)
    ) {
      groupedByBatch.push(element);
    } else {
      groupedByBatch[groupedByBatch.length - 1] = {
        ...lastGroupedItemByBatch,
        durationInSeconds: element.durationInSeconds + lastGroupedItemByBatch.durationInSeconds,
        endedOn: element.endedOn,
      };
    }
  });

  return { groupedByNCFile, groupedByBatch };
};

export const processTimelineResponse = (machineEvents: GroupedMachineEvent[], start: string, end: string): Omit<Events, 'loaded'> => {
  const events = machineEvents.map((item) => toEvent(item, start, end));
  const { groupedByNCFile, groupedByBatch } = groupEvents(events);
  return { events, groupedByNCFile, groupedByBatch };
};

export const mergeTimelineEvents = ({ existingEvents, newMachineEvents, start, end }: MergeTimelineEventsParams): Events => {
  const newEvents = newMachineEvents.map((item) => toEvent(item, start, end));

  const newEventKeys = new Set(newEvents.map(getEventKey));

  const deduplicatedExisting = existingEvents.filter((event) => !newEventKeys.has(getEventKey(event)));

  const combined = [...deduplicatedExisting, ...newEvents].sort((a, b) =>
    a.startedOn < b.startedOn ? -1 : a.startedOn > b.startedOn ? 1 : 0
  );

  const { groupedByNCFile, groupedByBatch } = groupEvents(combined);

  return {
    loaded: true,
    events: combined,
    groupedByNCFile,
    groupedByBatch,
  };
};
